import { Button } from '@suscri/ui';
import autoTable from 'jspdf-autotable';
import {
  Download,
  BarChart3,
  TrendingUp,
  PieChart,
  History,
  FileText,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePremiumAction } from '../components/PremiumAction';
import { PageHeader } from '../components/ui/PageHeader';
import i18n, { dateLocale } from '../lib/i18n';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

const EXPORT_ACTION = 'PDF_EXPORT'; // Exportación CSV/JSON: 10 créditos
const ADVANCED_REPORT_ACTION = 'ADVANCED_REPORT'; // Informe avanzado en PDF: 20 créditos
const EXTENDED_HISTORY_ACTION = 'EXTENDED_HISTORY'; // Rango > 12 meses: 15 créditos

const TWELVE_MONTHS_AGO = new Date();
TWELVE_MONTHS_AGO.setFullYear(TWELVE_MONTHS_AGO.getFullYear() - 1);

function downloadFile(
  filename: string,
  content: string | Blob,
  mimeType?: string
) {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// jspdf-autotable añade lastAutoTable a la instancia de jsPDF en runtime;
// sus tipos no lo declaran, así que lo modelamos aquí.
type DocWithAutoTable = { lastAutoTable: { finalY: number } };

function formatPct(value: number | null): string {
  if (value === null) return i18n.t('sin datos del periodo anterior');
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function ReportsPage() {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(),
  });
  // Rango pendiente de aplicar en el formulario (separado del aplicado, para
  // poder cobrar el histórico ampliado solo al confirmar, no en cada tecleo)
  const [pendingFrom, setPendingFrom] = useState(
    dateRange.from.toISOString().slice(0, 10)
  );

  const { run: runPremium, modalElement, actions } = usePremiumAction();
  const toast = useToast();
  const exportCost = actions?.find(a => a.code === EXPORT_ACTION)?.cost;
  const reportCost = actions?.find(
    a => a.code === ADVANCED_REPORT_ACTION
  )?.cost;
  const historyCost = actions?.find(
    a => a.code === EXTENDED_HISTORY_ACTION
  )?.cost;

  const { data: spendByCategory, isLoading: isLoadingCategory } =
    trpc.dashboard.spendByCategory.useQuery({
      from: dateRange.from,
      to: dateRange.to,
    });

  const { data: trend, isLoading: isLoadingTrend } =
    trpc.dashboard.trend.useQuery({
      granularity: 'monthly',
      from: dateRange.from,
      to: dateRange.to,
    });

  const { data: distribution, isLoading: isLoadingDistribution } =
    trpc.dashboard.distributionByProvider.useQuery({
      from: dateRange.from,
      to: dateRange.to,
    });

  const { data: comparison } = trpc.dashboard.comparison.useQuery();

  const applyDateRange = () => {
    const newFrom = new Date(pendingFrom);
    if (isNaN(newFrom.getTime())) {
      toast.error(t('Fecha no válida.'));
      return;
    }
    if (newFrom < TWELVE_MONTHS_AGO) {
      // Histórico ampliado: acción premium (15 créditos), cobrada vía consumeAction
      runPremium(EXTENDED_HISTORY_ACTION, () => {
        setDateRange(r => ({ ...r, from: newFrom }));
        toast.success(t('Histórico ampliado aplicado.'));
      });
    } else {
      setDateRange(r => ({ ...r, from: newFrom }));
    }
  };

  const generatePdfReport = async () => {
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString(dateLocale());

    doc.setFontSize(18);
    doc.text(t('Informe avanzado de suscripciones'), 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(
      `${t('Generado el')} ${today} · ${t('Periodo:')} ${dateRange.from.toLocaleDateString(dateLocale())} - ${dateRange.to.toLocaleDateString(dateLocale())}`,
      14,
      27
    );

    let y = 38;
    if (comparison) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(t('Comparativa de periodos'), 14, y);
      autoTable(doc, {
        startY: y + 4,
        head: [[t('Periodo'), t('Gasto'), t('Variación')]],
        body: [
          [
            t('Mes actual vs anterior'),
            `${comparison.currentMonthAmount.toFixed(2)} €`,
            formatPct(comparison.monthChangePercent),
          ],
          [
            t('Año actual vs anterior (YTD)'),
            `${comparison.currentYtdAmount.toFixed(2)} €`,
            formatPct(comparison.yearChangePercent),
          ],
        ],
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229] },
      });
      y = (doc as unknown as DocWithAutoTable).lastAutoTable.finalY + 10;
    }

    if (spendByCategory?.length) {
      doc.text(t('Gasto por categoría'), 14, y);
      autoTable(doc, {
        startY: y + 4,
        head: [[t('Categoría'), t('Importe')]],
        body: spendByCategory.map(i => [
          i.categoryName,
          `${i.amount.toFixed(2)} €`,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229] },
      });
      y = (doc as unknown as DocWithAutoTable).lastAutoTable.finalY + 10;
    }

    if (distribution?.length) {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.text(t('Distribución por proveedor'), 14, y);
      autoTable(doc, {
        startY: y + 4,
        head: [[t('Proveedor'), t('Importe')]],
        body: distribution.map(i => [
          i.providerName,
          `${i.amount.toFixed(2)} €`,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229] },
      });
      y = (doc as unknown as DocWithAutoTable).lastAutoTable.finalY + 10;
    }

    if (trend?.length) {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.text(t('Tendencia mensual'), 14, y);
      autoTable(doc, {
        startY: y + 4,
        head: [[t('Mes'), t('Importe')]],
        body: trend.map(i => [i.date, `${i.amount.toFixed(2)} €`]),
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229] },
      });
    }

    downloadFile(
      `informe-avanzado-suscri-${new Date().toISOString().slice(0, 10)}.pdf`,
      doc.output('blob')
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('Analítica')}
        title={t('Informes')}
        description={t('Analiza tus gastos y patrones de suscripción')}
        actions={<div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() =>
              runPremium(ADVANCED_REPORT_ACTION, generatePdfReport)
            }
          >
            <FileText className="mr-2 h-4 w-4" />
            {t('Informe avanzado (PDF)')}
            {reportCost ? ` · ${reportCost} cr.` : ''}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              runPremium(EXPORT_ACTION, () => {
                const rows = [
                  'seccion;concepto;importe',
                  ...(spendByCategory ?? []).map(
                    i => `categoria;${i.categoryName};${i.amount}`
                  ),
                  ...(distribution ?? []).map(
                    i => `proveedor;${i.providerName};${i.amount}`
                  ),
                  ...(trend ?? []).map(i => `tendencia;${i.date};${i.amount}`),
                ];
                downloadFile(
                  `informe-suscri-${new Date().toISOString().slice(0, 10)}.csv`,
                  rows.join('\n'),
                  'text/csv;charset=utf-8'
                );
              })
            }
          >
            <Download className="mr-2 h-4 w-4" />
            {t('Exportar CSV')}
            {exportCost ? ` · ${exportCost} cr.` : ''}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              runPremium(EXPORT_ACTION, () => {
                downloadFile(
                  `informe-suscri-${new Date().toISOString().slice(0, 10)}.json`,
                  JSON.stringify(
                    {
                      generado: new Date().toISOString(),
                      gastoPorCategoria: spendByCategory,
                      tendencia: trend,
                      distribucionPorProveedor: distribution,
                    },
                    null,
                    2
                  ),
                  'application/json'
                );
              })
            }
          >
            <Download className="mr-2 h-4 w-4" />
            {t('Exportar JSON')}
            {exportCost ? ` · ${exportCost} cr.` : ''}
          </Button>
        </div>}
      />
      {modalElement}

      {/* Selector de rango, con histórico ampliado premium más allá de 12 meses */}
      <div className="surface-card flex flex-wrap items-end gap-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-500">
            {t('Desde')}
          </label>
          <input
            type="date"
            value={pendingFrom}
            onChange={e => setPendingFrom(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-slate-800"
          />
        </div>
        <div className="text-sm text-gray-500">{t('hasta hoy')}</div>
        <Button variant="outline" onClick={applyDateRange}>
          <History className="mr-2 h-4 w-4" />
          {t('Aplicar')}
        </Button>
        <p className="text-xs text-gray-400">
          {t(
            'Los últimos 12 meses son gratuitos. Rangos más antiguos son histórico ampliado'
          )}
          {historyCost ? ` (${historyCost} ${t('créditos')})` : ''}.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="surface-card relative overflow-hidden p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                <PieChart className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('Gasto por Categoría')}
              </h3>
            </div>
            {isLoadingCategory ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-gray-200 animate-pulse rounded"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {spendByCategory?.slice(0, 5).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full shadow-sm"
                        style={{
                          backgroundColor: item.categoryColor || '#6B7280',
                        }}
                      />
                      <span className="text-sm font-semibold text-gray-800">
                        {item.categoryName}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      €{item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="surface-card relative overflow-hidden p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('Distribución por Proveedor')}
              </h3>
            </div>
            {isLoadingDistribution ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-gray-200 animate-pulse rounded"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {distribution?.slice(0, 5).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-sm font-semibold text-gray-800">
                      {item.providerName}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      €{item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="surface-card relative overflow-hidden p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center shadow-md">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('Tendencia Mensual')}
              </h3>
            </div>
            {isLoadingTrend ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-gray-200 animate-pulse rounded"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {trend?.slice(-6).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-sm font-semibold text-gray-800">
                      {item.date}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      €{item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="surface-card relative overflow-hidden p-6">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('Comparativa de Períodos')}
            </h3>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">
                {t('Mes Actual vs Mes Anterior')}
              </h4>
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {comparison ? formatPct(comparison.monthChangePercent) : '—'}
              </div>
              <p className="text-sm text-blue-700">
                {comparison
                  ? `${comparison.currentMonthAmount.toFixed(2)} € ${t('este mes')}`
                  : t('Cargando…')}
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2">
                {t('YTD vs Año Anterior')}
              </h4>
              <div className="text-3xl font-bold text-red-600 mb-2">
                {comparison ? formatPct(comparison.yearChangePercent) : '—'}
              </div>
              <p className="text-sm text-red-700">
                {comparison
                  ? `${comparison.currentYtdAmount.toFixed(2)} € ${t('en lo que va de año')}`
                  : t('Cargando…')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
